import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import {
  Box,
  Typography,
  useMediaQuery,
  Button,
  Card,
  CardContent,
  Skeleton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { Link as RouterLink } from "react-router-dom";
import MatchdayStrip from "./MatchdayStrip";
import { EmptyState, CardSkeletons } from "./ui/Pills";
import { listMatchdays, defaultMatchdayDate } from "../utils/matchdays";
import HighlightsCarousel from "./HighlightsCarousel";
import TeamFilter, { useTeamFilterParam } from "./TeamFilter";
import TeamCrest from "./ui/TeamCrest";
import ShareButton from "./ui/ShareButton";
import { fadeUp } from "./ui/motion";
import { torontoTodayKey, formatDayLabelToronto } from "./torontoDate";
import { OddsBar, pct } from "./insights/common";
import { withPredictedScores } from "../utils/sharePredictions";

// Goal scorers response: { [playerId]: goalCount }
function normalizeGoalScorers(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];
  return Object.entries(raw)
    .filter(([, count]) => typeof count === "number" && count > 0)
    .map(([playerId, goalCount]) => ({ playerId, goalCount }));
}

// Red cards: { [playerId]: count } or [playerId, ...]
function normalizeRedCards(raw) {
  if (Array.isArray(raw)) return raw.map(String);
  if (!raw || typeof raw !== "object") return [];
  return Object.entries(raw)
    .filter(([, v]) => v === true || (typeof v === "number" && v > 0))
    .map(([playerId]) => playerId);
}

function playerEvents(scorersRaw, redCardsRaw) {
  const byId = new Map();
  normalizeGoalScorers(scorersRaw).forEach(({ playerId, goalCount }) => {
    byId.set(playerId, { playerId, goalCount, redCard: false });
  });
  normalizeRedCards(redCardsRaw).forEach((playerId) => {
    const row = byId.get(playerId) || { playerId, goalCount: 0, redCard: false };
    row.redCard = true;
    byId.set(playerId, row);
  });
  return [...byId.values()];
}

const RedCardIcon = () => (
  <Box
    component="span"
    title="Red card"
    sx={{
      display: "inline-block",
      width: 8,
      height: 11,
      borderRadius: "1.5px",
      backgroundColor: "#d32f2f",
      flexShrink: 0,
    }}
  />
);


const TeamBlock = ({ team, dimmed, to }) => {
  const name = team?.name || "TBD";
  return (
    <Box
      {...(to ? { component: RouterLink, to, "aria-label": `${name} team page` } : {})}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: 0,
        textAlign: "center",
        textDecoration: "none",
        color: "inherit",
        borderRadius: 2,
        ...(to && { "&:hover .team-name": { textDecoration: "underline" } }),
      }}
    >
      <TeamCrest team={team} name={name} size={52} />
      <Typography
        className="team-name"
        sx={{
          mt: 0.75,
          width: "100%",
          fontSize: "0.82rem",
          fontWeight: 700,
          lineHeight: 1.25,
          color: dimmed ? "#8a8a8a" : "#1a1a1a",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          wordBreak: "break-word",
        }}
      >
        {name}
      </Typography>
    </Box>
  );
};

const LiveDot = () => (
  <Box
    component="span"
    sx={{
      width: 6,
      height: 6,
      borderRadius: "50%",
      bgcolor: "#16a34a",
      "@keyframes hslPulse": { "0%": { boxShadow: "0 0 0 0 rgba(22,163,74,0.6)" }, "100%": { boxShadow: "0 0 0 6px rgba(22,163,74,0)" } },
      animation: "hslPulse 1.4s ease-out infinite",
      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    }}
  />
);

const ScoreDigit = ({ value, dimmed }) => (
  <Typography
    component="span"
    sx={{
      minWidth: 30,
      textAlign: "center",
      fontSize: "2rem",
      fontWeight: 800,
      lineHeight: "40px",
      fontVariantNumeric: "tabular-nums",
      color: dimmed ? "#a8a8a8" : "#1a1a1a",
    }}
  >
    {value ?? 0}
  </Typography>
);

const EventList = ({ events, names, align }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 0.4,
      alignItems: align === "right" ? "flex-end" : "flex-start",
      minWidth: 0,
    }}
  >
    {events.map((e) => (
      <Box
        key={e.playerId}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          maxWidth: "100%",
          flexDirection: align === "right" ? "row-reverse" : "row",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, gap: 0.25 }}>
          {e.goalCount > 0 && <SportsSoccerIcon sx={{ fontSize: 12, color: "#1a1a1a" }} />}
          {e.goalCount > 1 && (
            <Typography component="span" sx={{ fontSize: "0.68rem", color: "#8a8a8a", fontWeight: 600 }}>
              ×{e.goalCount}
            </Typography>
          )}
          {e.redCard && <RedCardIcon />}
        </Box>
        <Typography noWrap sx={{ fontSize: "0.75rem", color: "#555", minWidth: 0 }}>
          {names[e.playerId] ?? "Unknown"}
        </Typography>
      </Box>
    ))}
  </Box>
);

const MatchCards = ({
  city,
  division,
  sport = "Soccer",
  ageGroup = "Adult",
  season = null,
  /** When true, load `youth_fixtures` / `youth_teams` (ignore city, division, sport, ageGroup filters). */
  youthMode = false,
}) => {
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playerNames, setPlayerNames] = useState({});
  const [suspensionsByFixture, setSuspensionsByFixture] = useState({});
  const [oddsByFixture, setOddsByFixture] = useState({});
  const [selectedTeamId, setSelectedTeamId] = useTeamFilterParam();
  const [dateParam, setDateParam] = useTeamFilterParam("date");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (youthMode) {
        const { data: teamRows, error: teamErr } = await supabase
          .from("youth_teams")
          .select("id, name, logo_url");
        if (teamErr) {
          console.error("Youth teams fetch:", teamErr);
          return [];
        }
        const teamMap = Object.fromEntries(
          (teamRows || []).map((t) => [t.id, { name: t.name, logo_url: t.logo_url || null }])
        );
        const { data: rawFx, error: fxErr } = await supabase
          .from("youth_fixtures")
          .select("*")
          .order("date", { ascending: true })
          .order("time", { ascending: true });
        if (fxErr) {
          console.error("Youth fixtures fetch:", fxErr);
          return [];
        }
        return (rawFx || []).map((f) => ({
          ...f,
          home_team: teamMap[f.home_team_id] || null,
          away_team: teamMap[f.away_team_id] || null,
        }));
      }

      if (!city || !division) return [];

      const baseSelect = `
          id,
          date,
          time,
          season,
          city,
          division,
          field,
          recordings,
          home_team_score,
          away_team_score,
          home_team_scorers,
          away_team_scorers,
          home_team_id,
          away_team_id,
          home_team:home_team_id (name, logo_url),
          away_team:away_team_id (name, logo_url),
          game_played,
          is_forfeit,
          home_team_red_cards,
          away_team_red_cards
        `;
      const selectWithoutGoalScorers = `
          id,
          date,
          time,
          season,
          city,
          division,
          field,
          recordings,
          home_team_score,
          away_team_score,
          home_team_id,
          away_team_id,
          home_team:home_team_id (name, logo_url),
          away_team:away_team_id (name, logo_url),
          game_played
        `;

      const buildQuery = (columns) => {
        let q = supabase
          .from("fixtures")
          .select(columns)
          .eq("city", city)
          .eq("sport", sport)
          .eq("age_group", ageGroup)
          .ilike("division", division)
          .order("date", { ascending: true })
          .order("time", { ascending: true });
        if (season) q = q.eq("season", season);
        return q;
      };

      let { data, error } = await buildQuery(baseSelect);
      if (error) {
        console.warn("Fixtures fetch with team_scorers failed (columns may not exist), retrying without:", error.message);
        const fallback = await buildQuery(selectWithoutGoalScorers);
        if (fallback.error) {
          console.error("Error fetching fixtures:", fallback.error);
          return [];
        }
        data = fallback.data;
      }
      return data || [];
    };

    setLoading(true);
    load().then((rows) => {
      if (cancelled) return;
      setFixtures(rows);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [city, division, sport, ageGroup, season, youthMode]);

  const todayKey = torontoTodayKey();
  const matchdays = useMemo(() => listMatchdays(fixtures), [fixtures]);
  const selectedDate = matchdays.some((m) => m.date === dateParam)
    ? dateParam
    : defaultMatchdayDate(matchdays, todayKey);
  const selectedMatchday = matchdays.find((m) => m.date === selectedDate);

  useEffect(() => {
    const ids = new Set();
    fixtures.forEach((match) => {
      [
        playerEvents(match.home_team_scorers, match.home_team_red_cards),
        playerEvents(match.away_team_scorers, match.away_team_red_cards),
      ].forEach((events) => events.forEach((e) => ids.add(e.playerId)));
    });
    if (ids.size === 0) {
      setPlayerNames({});
      return;
    }
    const fetchNames = async () => {
      const table = youthMode ? "youth_players" : "players";
      const { data, error } = await supabase.from(table).select("id, name").in("id", [...ids]);
      if (error) {
        console.error("Error fetching player names:", error);
        return;
      }
      const map = {};
      (data || []).forEach((p) => {
        map[p.id] = p.name || "Unknown";
      });
      setPlayerNames(map);
    };
    fetchNames();
  }, [fixtures, youthMode]);

  useEffect(() => {
    const upcomingIds = fixtures
      .filter((f) => !f.game_played && f.home_team_score == null)
      .map((f) => f.id);
    if (youthMode || upcomingIds.length === 0) {
      setSuspensionsByFixture({});
      return;
    }
    let cancelled = false;
    supabase
      .from("player_suspensions")
      .select("player_id, player_name, team_id, red_card_date, suspended_fixture_id")
      .in("suspended_fixture_id", upcomingIds)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.warn("Suspensions fetch failed:", error.message);
          setSuspensionsByFixture({});
          return;
        }
        const map = {};
        (data || []).forEach((s) => {
          (map[s.suspended_fixture_id] ||= []).push(s);
        });
        setSuspensionsByFixture(map);
      });
    return () => {
      cancelled = true;
    };
  }, [fixtures, youthMode]);

  useEffect(() => {
    const upcoming = fixtures.filter((f) => !f.is_forfeit && (f.home_team_score == null || f.away_team_score == null));
    if (youthMode || upcoming.length === 0) {
      setOddsByFixture({});
      return;
    }
    let cancelled = false;
    withPredictedScores(upcoming.map((f) => ({ fixture: f, score: null })))
      .then((games) => {
        if (cancelled) return;
        setOddsByFixture(Object.fromEntries(games.filter((g) => g.odds).map((g) => [g.fixture.id, g.odds])));
      })
      .catch((e) => {
        console.warn("Predictions fetch failed:", e?.message || e);
        if (!cancelled) setOddsByFixture({});
      });
    return () => {
      cancelled = true;
    };
  }, [fixtures, youthMode]);

  const convertTo12HourFormat = (time24) => {
    if (time24 == null || String(time24).trim() === "") return "—";
    const s = String(time24);
    const part = s.includes("T") ? s.split("T")[1] || s : s;
    const seg = part.split(":");
    const hours = seg[0];
    const minutes = seg[1] ?? "00";
    const period = +hours >= 12 ? "PM" : "AM";
    const hour12 = +hours % 12 || 12;
    return `${hour12}:${minutes.slice(0, 2)} ${period}`;
  };

  const formatDateLabel = (key) => {
    const today = torontoTodayKey();
    return key === today ? "Today" : formatDayLabelToronto(key, true);
  };

  const teamOptions = useMemo(() => {
    const byId = new Map();
    fixtures.forEach((match) => {
      [
        [match.home_team_id, match.home_team],
        [match.away_team_id, match.away_team],
      ].forEach(([id, team]) => {
        if (id == null || byId.has(id)) return;
        byId.set(id, {
          id,
          name: team?.name || `Team ${id}`,
          logo_url: team?.logo_url || null,
        });
      });
    });
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [fixtures]);

  const isTeamFiltered = teamOptions.some(
    (t) => String(t.id) === String(selectedTeamId)
  );

  const selectedTeamName = teamOptions.find(
    (t) => String(t.id) === String(selectedTeamId)
  )?.name;

  /** Drop a team that isn't in the loaded schedule (e.g. after a city / division change). */
  useEffect(() => {
    if (selectedTeamId && teamOptions.length > 0 && !isTeamFiltered) {
      setSelectedTeamId("");
    }
  }, [selectedTeamId, teamOptions.length, isTeamFiltered, setSelectedTeamId]);

  /** One team selected: show their whole schedule. Otherwise: one game day at a time. */
  const filteredFixtures = isTeamFiltered
    ? fixtures.filter(
        (match) =>
          String(match.home_team_id) === String(selectedTeamId) ||
          String(match.away_team_id) === String(selectedTeamId)
      )
    : fixtures.filter((match) => !selectedDate || match.date === selectedDate);

  const playedWithRecordings = filteredFixtures.filter(
    (match) =>
      match.home_team_score !== null &&
      match.away_team_score !== null &&
      match.recordings &&
      match.recordings.length > 0
  );

  const insightsHref =
    !youthMode && selectedMatchday && selectedMatchday.status !== "upcoming"
      ? `/insights?${new URLSearchParams({
          ...(season ? { season } : {}),
          city,
          division,
          matchday: String(selectedMatchday.matchday),
        })}`
      : null;

  if (loading) {
    return (
      <Box>
        <Box sx={{ display: "flex", gap: 1, justifyContent: "center", py: 2 }}>
          <Skeleton variant="rounded" width={140} height={36} sx={{ borderRadius: 999 }} />
        </Box>
        <Box sx={{ display: "flex", gap: 1, px: 2, pb: 1, overflow: "hidden" }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rounded" width={96} height={52} sx={{ borderRadius: 2.5, flexShrink: 0 }} />
          ))}
        </Box>
        <CardSkeletons count={isMobile ? 2 : 4} />
      </Box>
    );
  }

  if (fixtures.length === 0) {
    return (
      <EmptyState
        title={youthMode ? "Youth fixtures are coming soon" : `No fixtures published${division ? ` for ${division}` : ""} yet`}
        body="The schedule appears here as soon as it's released. Follow @hadafsoccerleague for announcements."
        action={{ label: "Register for a league", to: "/register/select-division" }}
      />
    );
  }

  return (
    <Box sx={{ minHeight: "60vh" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          px: 1,
          pt: 2,
          pb: isTeamFiltered ? 0 : 0.5,
        }}
      >
        <TeamFilter
          teams={teamOptions}
          value={selectedTeamId}
          onChange={setSelectedTeamId}
          allLabel="All teams"
        />
        {isTeamFiltered && !youthMode && (
          <Button
            component={RouterLink}
            to={`/team/${selectedTeamId}`}
            size="small"
            sx={{ textTransform: "none", fontWeight: 700, color: "#111", borderRadius: 999 }}
          >
            Team page →
          </Button>
        )}
        {isTeamFiltered && !youthMode && (
          <ShareButton
            kind="matchday"
            variant="dark"
            label="Share schedule"
            data={{
              title: `${selectedTeamName} schedule`,
              subtitle: [city, division].filter(Boolean).join(" · "),
              division,
              city,
              games: filteredFixtures.map((m) => ({
                home: m.home_team,
                away: m.away_team,
                fixture: m,
                score: m.is_forfeit ? "FF" : m.home_team_score != null && m.away_team_score != null ? `${m.home_team_score}–${m.away_team_score}` : null,
                time: formatDayLabelToronto(m.date, true),
              })),
            }}
          />
        )}
      </Box>

      {isTeamFiltered ? (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "center",
            color: "#666",
            pt: 1,
            pb: 0.5,
            px: 2,
          }}
        >
          {selectedTeamName} — full schedule ({filteredFixtures.length}{" "}
          {filteredFixtures.length === 1 ? "game" : "games"})
        </Typography>
      ) : (
        <>
          <MatchdayStrip
            matchdays={matchdays}
            selectedDate={selectedDate}
            onSelect={setDateParam}
            todayKey={todayKey}
          />
          {selectedMatchday && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: 1, px: 2 }}>
              <Typography sx={{ fontSize: "0.8rem", color: "#666" }}>
                Matchday {selectedMatchday.matchday} of {matchdays.length} · {selectedMatchday.games}{" "}
                {selectedMatchday.games === 1 ? "game" : "games"}
                {selectedMatchday.status === "partial" ? ` · ${selectedMatchday.played} played` : ""}
              </Typography>
              {insightsHref && (
                <Button
                  component={RouterLink}
                  to={insightsHref}
                  size="small"
                  sx={{ textTransform: "none", fontWeight: 700, color: "#111", borderRadius: 999, py: 0 }}
                >
                  Matchday insights →
                </Button>
              )}
              {!youthMode && (
                <ShareButton
                  kind="matchday"
                  variant="dark"
                  label="Share"
                  data={{
                    title: `Matchday ${selectedMatchday.matchday}`,
                    subtitle: [city, division, formatDayLabelToronto(selectedMatchday.date, true)].filter(Boolean).join(" · "),
                    division,
                    city,
                    games: filteredFixtures.map((m) => ({
                      home: m.home_team,
                      away: m.away_team,
                      fixture: m,
                      score: m.is_forfeit ? "FF" : m.home_team_score != null && m.away_team_score != null ? `${m.home_team_score}–${m.away_team_score}` : null,
                      time: convertTo12HourFormat(m.time),
                    })),
                  }}
                  sx={{ py: 0.25 }}
                />
              )}
            </Box>
          )}
        </>
      )}

      <Box
        sx={{
          px: isMobile ? 1 : 2,
          py: 2,
          display: "grid",
          gap: 2,
          gridTemplateColumns: isMobile
            ? "1fr"
            : "repeat(auto-fit, minmax(300px, 1fr))",
        }}
      >
        {filteredFixtures.map((match, index) => {
          const isForfeit = match.is_forfeit === true;
          const isPlayed =
            isForfeit ||
            (match.home_team_score !== null && match.away_team_score !== null);

          const homeEvents = playerEvents(match.home_team_scorers, match.home_team_red_cards);
          const awayEvents = playerEvents(match.away_team_scorers, match.away_team_red_cards);
          const hasEvents = isPlayed && (homeEvents.length > 0 || awayEvents.length > 0);
          const suspended = suspensionsByFixture[match.id] || [];
          const odds = isPlayed ? null : oddsByFixture[match.id];

          const hs = Number(match.home_team_score);
          const as = Number(match.away_team_score);
          const homeLost = isPlayed && !isForfeit && hs < as;
          const awayLost = isPlayed && !isForfeit && as < hs;

          const status = isForfeit
            ? { label: "Forfeit", color: "#b91c1c", bg: "#fee2e2" }
            : isPlayed
            ? { label: "Final", color: "#fff", bg: "#18181b" }
            : match.date === todayKey
            ? { label: "Today", color: "#15803d", bg: "#dcfce7", live: true }
            : { label: "Upcoming", color: "#52525b", bg: "#f4f4f5" };

          return (
            <Card
              key={match.id}
              elevation={0}
              sx={{
                backgroundColor: "#fff",
                color: "#1a1a1a",
                border: "1px solid #ececec",
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 4px 14px rgba(16,24,40,0.05)",
                transition: "box-shadow 0.2s ease, transform 0.2s ease",
                ...fadeUp(index),
                "&:hover": {
                  boxShadow: "0 2px 4px rgba(16,24,40,0.06), 0 10px 24px rgba(16,24,40,0.08)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  px: 2,
                  py: 1,
                  backgroundColor: "#fafafa",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <Typography noWrap sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#444", minWidth: 0 }}>
                  {formatDateLabel(match.date)} · {convertTo12HourFormat(match.time)}
                </Typography>
                {match.field && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0, color: "#777" }}>
                    <PlaceOutlinedIcon sx={{ fontSize: 14, flexShrink: 0 }} />
                    <Typography noWrap sx={{ fontSize: "0.72rem", color: "inherit" }}>
                      {match.field}
                    </Typography>
                  </Box>
                )}
              </Box>

              <CardContent sx={{ px: 2, pt: 2, pb: 2, "&:last-child": { pb: 2 } }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
                    alignItems: "start",
                    columnGap: 1.5,
                  }}
                >
                  <TeamBlock team={match.home_team} dimmed={homeLost} to={!youthMode && match.home_team_id ? `/team/${match.home_team_id}` : null} />
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: 0.75, minWidth: 92 }}>
                    {isPlayed ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, lineHeight: 1 }}>
                        <ScoreDigit value={match.home_team_score} dimmed={homeLost} />
                        <Typography component="span" sx={{ color: "#c4c4c4", fontWeight: 600, fontSize: "1.25rem" }}>
                          –
                        </Typography>
                        <ScoreDigit value={match.away_team_score} dimmed={awayLost} />
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, color: "#bdbdbd", letterSpacing: "0.08em", lineHeight: "40px" }}>
                        VS
                      </Typography>
                    )}
                    <Box
                      component="span"
                      sx={{
                        mt: 1,
                        px: 1.25,
                        py: 0.25,
                        borderRadius: 999,
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                        color: status.color,
                        backgroundColor: status.bg,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {status.live && <LiveDot />}
                      {status.label}
                    </Box>
                  </Box>
                  <TeamBlock team={match.away_team} dimmed={awayLost} to={!youthMode && match.away_team_id ? `/team/${match.away_team_id}` : null} />
                </Box>

                {isForfeit && (
                  <Typography sx={{ mt: 1.5, textAlign: "center", fontSize: "0.72rem", color: "#8a8a8a" }}>
                    Recorded 0–0 · no points awarded to either team
                  </Typography>
                )}

                {odds && (
                  <Box
                    aria-label={`Prediction: ${match.home_team?.name || "Home"} win ${pct(odds.home)}, draw ${pct(odds.draw)}, ${match.away_team?.name || "Away"} win ${pct(odds.away)}`}
                    sx={{ mt: 1.75, pt: 1.25, borderTop: "1px solid #f2f2f2", display: "flex", flexDirection: "column", gap: 0.75 }}
                  >
                    <Box sx={{ display: "flex" }}>
                      <OddsBar home={odds.home} draw={odds.draw} away={odds.away} height={8} />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "0.7rem", fontWeight: 600, color: "#777" }}>
                      <span>Win {pct(odds.home)}</span>
                      <span>Draw {pct(odds.draw)}</span>
                      <span>Win {pct(odds.away)}</span>
                    </Box>
                  </Box>
                )}

                {hasEvents && (
                  <Box
                    sx={{
                      mt: 1.75,
                      pt: 1.5,
                      borderTop: "1px solid #f2f2f2",
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                      columnGap: 2,
                    }}
                  >
                    <EventList events={homeEvents} names={playerNames} align="right" />
                    <EventList events={awayEvents} names={playerNames} align="left" />
                  </Box>
                )}

                {!isPlayed && suspended.length > 0 && (
                  <Box
                    sx={{
                      mt: 1.75,
                      pt: 1.25,
                      borderTop: "1px solid #f2f2f2",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    {suspended.map((sp) => {
                      const teamName =
                        sp.team_id === match.home_team_id
                          ? match.home_team?.name
                          : match.away_team?.name;
                      return (
                        <Box
                          key={`${sp.player_id}-${sp.red_card_date}`}
                          sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75, minWidth: 0 }}
                        >
                          <RedCardIcon />
                          <Typography noWrap sx={{ fontSize: "0.75rem", color: "#555", minWidth: 0 }}>
                            <Box component="span" sx={{ fontWeight: 700, color: "#c62828" }}>Suspended</Box>
                            {" · "}
                            {sp.player_name}
                            {teamName ? ` (${teamName})` : ""}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {(isMobile || !isPlayed) && match.recordings?.length > 0 && (
                  <Box mt={2} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {match.recordings.map((link, idx) => (
                      <Button
                        key={idx}
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={() => window.open(link, "_blank")}
                        sx={{
                          color: "#000",
                          borderColor: "#e0e0e0",
                          borderRadius: 2,
                          textTransform: "none",
                          fontSize: "0.8rem",
                          "&:hover": { borderColor: "#333", backgroundColor: "rgba(0,0,0,0.04)" },
                        }}
                      >
                        {match.recordings.length === 1
                          ? "Watch Game Recording"
                          : `Watch Game Recording ${idx + 1}`}
                      </Button>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {!isMobile && playedWithRecordings.length > 0 && (
        <HighlightsCarousel matches={playedWithRecordings} />
      )}
    </Box>
  );
};

export default MatchCards;
